/**
 * Per-list local edits (quantity / selected / removed) for the Your Lists page.
 *
 * When a user edits a list in the detail view but hasn't saved yet, the changes
 * are kept in sessionStorage. Both the detail view and the dashboard read these
 * so an "add list to cart" from the dashboard uses the user's unsaved edits.
 *
 * COB2B-785: drafts only last while the page stays loaded (moving between the
 * dashboard and a list). Every load of Your Lists — a refresh included — starts
 * from the saved lists: a draft carried over could be applied to a list that
 * changed in the meantime (e.g. a product added from a PLP), and its `removed`
 * would hide items that are really there. The entry clears them on load.
 */

const editsKey = (listId) => `your-lists:edits:${listId}`;

/** Load the local draft edits for a list, or null if none. */
export function loadEdits(listId) {
  try {
    return JSON.parse(sessionStorage.getItem(editsKey(listId))) || null;
  } catch (e) {
    return null;
  }
}

/** Persist the local draft edits for a list. */
export function saveEdits(listId, edits) {
  try {
    sessionStorage.setItem(editsKey(listId), JSON.stringify(edits));
  } catch (e) {
    // storage unavailable / full — non-fatal
  }
}

/** Drop every list's local draft. */
export function clearAllEdits() {
  try {
    const prefix = editsKey('');
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (e) {
    // storage unavailable — non-fatal
  }
}

/**
 * Drop the local draft — call after edits are persisted to the backend
 * (e.g. "Save and update cart" succeeds), so a refresh shows the saved state.
 */
export function clearEdits(listId) {
  try {
    sessionStorage.removeItem(editsKey(listId));
  } catch (e) {
    // storage unavailable — non-fatal
  }
}

/**
 * Merge a list's stored items with the given local draft edits: drop removed
 * items, apply per-item quantity and selected state. Returns a new array; if
 * there are no edits the items are returned unchanged.
 *
 * @param {Array<{ id, quantity?, selected? }>} items list.items
 * @param {{ quantities?: Object, selected?: Array, removed?: Array }|null} edits
 */
export function applyEditsToItems(items, edits) {
  const list = Array.isArray(items) ? items : [];
  if (!edits) return list;

  const removed = new Set(edits.removed || []);
  const selected = new Set(edits.selected || []);
  const quantities = edits.quantities || {};

  return list
    .filter((it) => !removed.has(it.id))
    .map((it) => ({
      ...it,
      quantity: quantities[it.id] ?? it.quantity ?? 0,
      selected: selected.has(it.id),
    }));
}
